import { formatRM } from '@/features/billing/money';
import type { Appointment } from '@/features/queue/types';
import { isApiError } from '@/lib/api';
import { klTime, whenLabel } from './time';

export const STATUS_LABEL: Record<Appointment['status'], string> = {
  booked: 'Booked',
  checked_in: 'Arrived',
  cancelled: 'Cancelled',
  no_show: 'No-show',
};

export function depositLabel(a: Appointment) {
  if (!a.deposit) return '-';
  const amount = formatRM(a.deposit.amountSen);
  return {
    pending: `${amount} · due`,
    paid: `${amount} · paid`,
    refunded: `${amount} · refunded`,
    forfeited: `${amount} · kept`,
  }[a.deposit.status];
}

/**
 * RL-1044 / RL-1045: the API refuses a booking (new, rescheduled or from the
 * waitlist) that overlaps another booking of the same patient or of the
 * chosen doctor (409 APPOINTMENT_PATIENT_CONFLICT / APPOINTMENT_DOCTOR_CONFLICT,
 * with `conflictingAppointment` { id, startsAt, endsAt }). Returns the title
 * and a message that names the time already taken, else null.
 */
export const BOOKING_CONFLICT_CODES = [
  'APPOINTMENT_PATIENT_CONFLICT',
  'APPOINTMENT_DOCTOR_CONFLICT',
] as const;

export function bookingConflict(
  err: unknown,
): { title: string; message: string } | null {
  if (
    !isApiError(err) ||
    !(BOOKING_CONFLICT_CODES as readonly string[]).includes(err.code)
  )
    return null;
  const other = err.problem?.conflictingAppointment as
    { startsAt?: string; endsAt?: string } | undefined;
  const patient = err.code === 'APPOINTMENT_PATIENT_CONFLICT';
  const title =
    err.problem?.title ??
    (patient
      ? 'Patient already booked at this time'
      : 'Doctor already booked at this time');
  const taken =
    other?.startsAt && other.endsAt
      ? ` ${patient ? "The patient's" : "The doctor's"} other booking: ${whenLabel(other.startsAt)} to ${klTime(other.endsAt)}.`
      : '';
  return { title, message: `${err.message}${taken}` };
}
